import { CdnAsset } from "#components/ui/CdnAsset.tsx";
import { channels } from "#features/conversations/types/channels.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Image, type ImageProps } from "@hopper-ui/components";
import { type SyntheticEvent, useCallback, useMemo, useState } from "react";

interface Props extends Partial<ImageProps> {
    url: string;
}

export function WebsiteFavicon({ url, width, height, className }: Props) {
    const { tShared } = useSharedTranslation();
    const [isError, setIsError] = useState(false);

    const size = useMemo(() => typeof width === "number" ? width * 2 : 48, [width]);

    const faviconUrl = useMemo(() => {
        return `https://www.google.com/s2/favicons?domain=${url}&sz=${size}`;
    }, [url, size]);

    const handleLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
        // Make sure to show our own fallback icon instead of the default one from Google
        // Compare the required size with the actual size of the image to check if the favicon was loaded correctly
        // The google favicon service returns a 16x16 image when the favicon is not found
        // The size computed prop is never 16x16, so we can use this to check if the image is valid
        if (event.currentTarget.naturalWidth !== size) {
            setIsError(true);
        }
    }, [size]);

    if (!isError) {
        return (
            <Image
                width={width}
                height={height}
                className={className}
                src={faviconUrl}
                alt={tShared("websiteIcon")}
                onError={() => setIsError(true)}
                onLoad={handleLoad}
            />
        );
    }

    const { iconUrl, nameKey } = channels.WebCrawler;

    return (
        <CdnAsset
            asset={iconUrl}
            alt={tShared(nameKey)}
            width={width}
            height={height}
        />
    );
}
